// src/app/shared/interceptors/auth-error.interceptor.ts
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { catchError, share, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { SingleActionDialogComponent } from '../components/dialogs/single-action-dialog/single-action-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../services/shared-storage.service';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private isShowingLogoutDialog = false;
  private pendingLogoutRequests = new Set<string>(); // 追蹤待處理的請求

  constructor(
    private injector: Injector, 
    private authService: AuthService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err); // 不是 HTTP 錯誤直接丟
        }

        // // 排除 refresh sessions API，避免無限循環
        // const IGNORE_AUTH_URLS = [
        //   '/member/refreshsessions',
        //   '/member/sessionslogout'
        // ];
        // if (IGNORE_AUTH_URLS.some(url => req.url.includes(url))) {
        //   return next.handle(req);
        // }

        console.log('error:', err);

        // 檢查是否為 token 過期錯誤
        if (err?.error?.statusCode === 'EC6008') {
          console.log('EC6008，重新取得有效token');

          // 解決同時間多個請求同時刷新token的問題
          // return this.authService.handleTokenRefresh(req, (request) => next.handle(request));
          
          return this.authService.getValidToken().pipe(
            switchMap((authResponse) => {
              if (!authResponse?.data?.accessToken) {
                // 無法取得新 token，執行登出
                console.log('無法取得新 token，執行登出');
                const auth = this.injector.get(AuthService);
                const router = this.injector.get(Router);
                auth.performLogout().subscribe({
                  next: (res) => {
                    auth.clearToken();
                    console.log('登出 API 成功:', res);
                  },
                  error: (error) => {
                    auth.clearToken();
                    console.error('登出 API 失敗:', error);
                  }
                });
                router.navigate([''], { replaceUrl: true });
                return EMPTY;
              }

              console.log("new token:", authResponse.data.accessToken);
              this.authService.setToken(authResponse.data.accessToken);

              // 用新 token 重新發送原始請求
              const newAuthReq = req.clone({
                setHeaders: { Authorization: `Bearer ${authResponse.data.accessToken}` }
              });
              return next.handle(newAuthReq);
            }),
            catchError(() => {
              // refresh token 失效，執行登出
              const auth = this.injector.get(AuthService);
              const router = this.injector.get(Router);
              auth.performLogout().subscribe({
                next: (res) => {
                  auth.clearToken();
                  console.log('登出 API 成功:', res);
                },
                error: (error) => {
                  auth.clearToken();
                  console.error('登出 API 失敗:', error);
                }
              });
              router.navigate([''], { replaceUrl: true });
              return EMPTY;
            })
          );
        }

        // 已在其他裝置登入(異地登入被登出)
        if (err?.error?.statusCode === 'EC6010' || err?.error?.errorMessage === '已在其他裝置登入') {
          const translateService = this.injector.get(TranslateService);
          const sharedStorageService = this.injector.get(SharedStorageService);
          const dialog = this.injector.get(MatDialog);
          const auth = this.injector.get(AuthService);
          const router = this.injector.get(Router);

          // 關閉所有打開的對話框（包括全日檢又沒選午餐就呼叫餐食dialog- modifyExamDietDialogRef）
          dialog.closeAll();

          auth.clearToken();
          sharedStorageService.clear();
          sharedStorageService.remove('fromIdLogin');
          sharedStorageService.remove('myReservation');
          sharedStorageService.remove('savedQuestionnaireData');
          router.navigate([''], { replaceUrl: true });

          const hint  = dialog.open(SingleActionDialogComponent, {
            disableClose: true,
            data: {
              title: translateService.instant('otp-dialog.single-action-title'),
              message: translateService.instant('otp-dialog.single-action-message'),
              showCloseButton: false,
              buttonText: translateService.instant('otp-dialog.single-action-button-text'),
              action: () => hint.close(),
            },
          });
          return EMPTY;
        }
        // 已在其他地方登入
        if (err?.error?.statusCode === 'E9028') {
          const auth = this.injector.get(AuthService);
          const router = this.injector.get(Router);
          const sharedStorageService = this.injector.get(SharedStorageService);
          sharedStorageService.clear();
          sharedStorageService.remove('fromIdLogin');
          sharedStorageService.remove('myReservation');
          sharedStorageService.remove('savedQuestionnaireData');
          auth.clearToken();
          router.navigate(['/healthcheck/idlogin'], { replaceUrl: true });
          return EMPTY;
        }
        // 401 沒帶 token or 無效 or 過期，先清掉再導回首頁
        if (err.status === 401 || err?.error?.statusCode === 'EC6009' || err?.error?.statusCode === 'EC6007') {
          const requestKey = `${req.method}-${req.url}`;
          
          // 如果這個請求已經在處理中,直接返回
          if (this.pendingLogoutRequests.has(requestKey)) {
            return EMPTY;
          }
          
          // 如果已經在顯示彈窗,直接返回
          if (this.isShowingLogoutDialog) {
            return EMPTY;
          }

          // 標記這個請求正在處理
          this.pendingLogoutRequests.add(requestKey);
          
          const auth = this.injector.get(AuthService);
          const router = this.injector.get(Router);
          const dialog = this.injector.get(MatDialog);
          const translateService = this.injector.get(TranslateService);
          const sharedStorageService = this.injector.get(SharedStorageService);

          router.navigate([''], { replaceUrl: true });

          translateService.get([
            'login-register.automatically-logout.title',
            'login-register.automatically-logout.message',
            'common.button.i-understood'
          ]).subscribe(translations => {
            const fromIdLogin = sharedStorageService.get('fromIdLogin');
            
            if (fromIdLogin === true) {
              console.log('在 idLogin 頁面，不顯示彈窗，背景清理並重新載入');
              sharedStorageService.clear();
              sharedStorageService.remove('fromIdLogin');
              sharedStorageService.remove('myReservation');
              sharedStorageService.remove('savedQuestionnaireData');
              auth.clearToken();
              this.pendingLogoutRequests.clear();
              return;
            }

            // 設置彈窗標記
            this.isShowingLogoutDialog = true;

            const hint = dialog.open(SingleActionDialogComponent, {
              disableClose: true,
              data: {
                title: translations['login-register.automatically-logout.title'],
                message: translations['login-register.automatically-logout.message'],
                showCloseButton: false,
                buttonText: translations['common.button.i-understood'],
                action: () => {
                  sharedStorageService.clear();
                  sharedStorageService.remove('fromIdLogin');
                  sharedStorageService.remove('myReservation');
                  sharedStorageService.remove('savedQuestionnaireData');
                  auth.clearToken();
                  hint.close();
                },
              },
            });

            hint.afterClosed().subscribe(() => {
              this.isShowingLogoutDialog = false;
              this.pendingLogoutRequests.clear(); // 清空待處理請求
              window.location.href = '/healthcheck'; // 只用一種導航
            });
          });

          return EMPTY;
        }

        return throwError(() => err); // 其它錯誤往外丟
      })
    );
  }
}
